import { AlertTriangle, Copy, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '~/components/ui/button';
import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';
import { copyToClipboard } from '~/utils/clipboard';
import { cn } from '~/utils/cn';

import {
  isAccessProblem,
  problemFallbackKey,
  problemTitleKey,
  readProblem,
} from '../../utils/taxonomy-errors';

interface QueryErrorProps {
  error: unknown;
  onRetry?: (() => void) | undefined;
  /** Overrides the friendly title (e.g. "This node no longer exists in R14"). */
  title?: string | undefined;
  /** Smaller inline variant for rows / popovers. */
  compact?: boolean;
  className?: string;
}

/**
 * Error state for a panel whose read failed: friendly title, the server's `detail`, the
 * support reference (copyable) and Retry. Access problems (401 / NOT_PLATFORM_ADMIN) are
 * escalated to the layout's full-page message instead of repeating in every panel.
 */
export function QueryError({ error, onRetry, title, compact = false, className }: QueryErrorProps) {
  const { t } = useTranslation('taxonomy-studio');
  const p = readProblem(error);
  const access = isAccessProblem(p);
  const notInitialized = p.code === 'NOT_INITIALIZED';

  useEffect(() => {
    const store = useTaxonomyStudioStore.getState();
    if (access) store.setAccessProblem({ status: p.status, code: p.code, detail: p.detail });
    if (notInitialized) store.setNotInitialized(true);
  }, [access, notInitialized, p.status, p.code, p.detail]);

  if (access || notInitialized) return null;
  const heading = title ?? t(problemTitleKey(p), { defaultValue: t('errors.title.UNKNOWN') });
  const detail = p.detail || t(problemFallbackKey(p));

  return (
    <div
      role="alert"
      className={cn(
        'border-error/30 bg-error-50/60 flex items-start gap-3 rounded-lg border text-sm',
        compact ? 'p-2.5' : 'p-4',
        className,
      )}
    >
      <AlertTriangle className="text-error mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-foreground font-medium">{heading}</p>
        <p className="text-muted-foreground break-words">{detail}</p>
        {p.requestId && (
          <p className="text-muted-foreground flex items-center gap-1 text-xs">
            {t('errors.reference', { id: p.requestId })}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 cursor-pointer"
              aria-label={t('errors.action.copyReference')}
              title={t('errors.action.copyReference')}
              onClick={() => {
                if (p.requestId) copyToClipboard(p.requestId);
                toast.success(t('errors.copied'));
              }}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </p>
        )}
      </div>
      {onRetry && (
        <Button size="sm" variant="outline" className="shrink-0 cursor-pointer" onClick={onRetry}>
          <RefreshCw />
          {t('errors.action.retry')}
        </Button>
      )}
    </div>
  );
}
